/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, addDoc, serverTimestamp, query, where, doc, updateDoc, deleteDoc, orderBy, getDoc } from 'firebase/firestore';
import { Fish, Loader2, Edit, Trash2, Tent, Waves, Search, ArrowLeft, LogOut, Share2, Flag, X, Archive } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';

// --- Constants & Config ---
const ADMIN_USER_ID = 'm8dOaJPfkuZ1rtYxG6rnV3GOmKh2'; 

// --- Firebase Context & Provider ---
const FirebaseContext = createContext(null);
const useFirebase = () => useContext(FirebaseContext);

const FirebaseConfigError = () => (
    <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center p-8 bg-white shadow-lg rounded-lg max-w-lg mx-4">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Firebase Configuration Error</h2>
            <p className="text-gray-700 mb-2">
                The application could not connect to Firebase because the necessary configuration credentials were not found.
            </p>
            <p className="text-gray-700 mb-4">
                This is an issue with the environment settings, not the code itself.
            </p>
            <div className="text-left bg-gray-50 p-4 rounded">
                <h3 className="font-bold text-gray-800 mb-2">What to do next:</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Ensure your Firebase project is correctly linked in this platform's settings.</li>
                    <li>Verify that the Project ID (<strong>gooutside-9d16a</strong>) is correct.</li>
                    <li>Try unlinking and relinking the Firebase project.</li>
                </ul>
            </div>
        </div>
    </div>
);

const FirebaseProvider = ({ children }) => {
    const [app, setApp] = useState(null);
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appId, setAppId] = useState('default-app-id');
    const [configError, setConfigError] = useState(false);

    useEffect(() => {
        const getFirebaseConfig = () => {
            if (typeof __firebase_config !== 'undefined' && __firebase_config && __firebase_config !== '{}') {
                return JSON.parse(__firebase_config);
            }
            return {
                apiKey: "AIzaSyAqBqR0jrvkRZIIJhhgc7uHcZAR8Vmxq8o",
                authDomain: "gooutside-9d16a.firebaseapp.com",
                projectId: "gooutside-9d16a",
                storageBucket: "gooutside-9d16a.firebasestorage.app",
                messagingSenderId: "1062309205541",
                appId: "1:1062309205541:web:ef994753add9c1cb13d1cc",
                measurementId: "G-EWKPK0HXPC"
            };
        };

        const firebaseConfig = getFirebaseConfig();

        try {
            const currentAppId = typeof __app_id !== 'undefined' ? __app_id : firebaseConfig.projectId;
            setAppId(currentAppId);

            if (!firebaseConfig.projectId) {
                setConfigError(true);
                setLoading(false);
                return;
            }

            const firebaseApp = initializeApp(firebaseConfig);
            const firestore = getFirestore(firebaseApp);
            const firebaseAuth = getAuth(firebaseApp);

            setApp(firebaseApp);
            setDb(firestore);
            setAuth(firebaseAuth);

            onAuthStateChanged(firebaseAuth, async (currentUser) => {
                if (currentUser) {
                    setUser(currentUser);
                } else {
                    if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
                        await signInWithCustomToken(firebaseAuth, __initial_auth_token);
                    } else {
                        await signInAnonymously(firebaseAuth);
                    }
                }
                setLoading(false); 
            });

        } catch (error) {
            console.error("Error during Firebase initialization:", error);
            setConfigError(true);
            setLoading(false);
        }
    }, []); 

    const value = { app, db, auth, user, loading, appId, isAdmin: user?.uid === ADMIN_USER_ID };

    if (configError) {
        return <FirebaseConfigError />;
    }

    return (
        <FirebaseContext.Provider value={value}>
            {loading ? <GlobalLoader /> : children}
        </FirebaseContext.Provider>
    );
};

// --- Custom Hooks ---
function useProducts(collectionName, category) {
    const { db, appId } = useFirebase();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db || !appId || appId === 'default-app-id') {
            if (appId === 'default-app-id') setLoading(false);
            return;
        };
        setLoading(true);

        const productsCollectionRef = collection(db, `artifacts/${appId}/public/data/${collectionName}`);
        let q;
        
        const orderByField = collectionName === 'expiredProducts' ? 'expiredAt' : 'createdAt';

        if (category) {
            q = query(productsCollectionRef, where('category', 'array-contains', category), orderBy(orderByField, 'desc'));
        } else {
            q = query(productsCollectionRef, orderBy(orderByField, 'desc'));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(productsData);
            setLoading(false);
        }, (error) => {
            console.error(`Error fetching from ${collectionName}:`, error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, appId, collectionName, category]);

    return { products, loading };
}

// --- UI Components ---
const GlobalLoader = () => (
    <div className="flex justify-center items-center h-screen bg-gray-100">
        <Loader2 className="animate-spin text-green-700" size={64} />
    </div>
);

const Header = ({ searchTerm, setSearchTerm }) => {
    const { auth, isAdmin } = useFirebase();
    const navigate = useNavigate();

    const handleLogout = async () => {
        if (auth) {
            await signOut(auth);
            navigate('/login');
        }
    };

    return (
        <header className="bg-green-800 bg-opacity-80 text-white p-4 shadow-lg sticky top-0 z-40">
            <div className="container mx-auto flex flex-col md:flex-row items-center justify-between">
                <Link to="/" className="mb-4 md:mb-0">
                    <img
                        src="https://lh3.googleusercontent.com/d/1i4MYY7X08bRBlwpK2_U9rtpTrlfRHRlm"
                        alt="GoOutside! Logo"
                        className="h-16 w-auto object-contain"
                        onError={(e) => { e.target.src = "https://placehold.co/150x60/cccccc/333333?text=GoOutside+Logo"; }}
                    />
                </Link>
                <div className="flex items-center gap-2 sm:gap-4 mt-4 md:mt-0 md:ml-6">
                    <div className="relative w-full sm:w-60">
                        <input type="text" placeholder="Search products..." className="w-full pl-10 pr-4 py-2 rounded-full bg-white bg-opacity-90 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-inner" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                    </div>
                    {isAdmin && (
                        <>
                            <Link to="/manage-articles" className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 transition-colors duration-300 whitespace-nowrap">Articles</Link>
                            <Link to="/banners" className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 transition-colors duration-300 whitespace-nowrap">Banners</Link>
                            <Link to="/deal-reports" className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 transition-colors duration-300 whitespace-nowrap">Reports</Link>
                            <button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 transition-colors duration-300"><LogOut size={18} /> Logout</button>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};

const CategoryNav = () => (
    <div className="bg-orange-500 bg-opacity-80 shadow-lg sticky top-[168px] md:top-[96px] z-30">
        <nav className="container mx-auto px-4 py-1">
            <ul className="flex flex-nowrap justify-center items-center space-x-2 sm:space-x-4 text-sm sm:text-base">
                <li><Link to="/fishing-deals" className="text-white font-semibold hover:text-green-900 transition duration-300 p-2 rounded-md whitespace-nowrap">Fishing</Link></li>
                <li><Link to="/camping-deals" className="text-white font-semibold hover:text-green-900 transition duration-300 p-2 rounded-md whitespace-nowrap">Camping</Link></li>
                <li><Link to="/water-sports-deals" className="text-white font-semibold hover:text-green-900 transition duration-300 p-2 rounded-md whitespace-nowrap">Water Sports</Link></li>
                <li><Link to="/blog" className="text-white font-semibold hover:text-green-900 transition duration-300 p-2 rounded-md whitespace-nowrap">Blog</Link></li>
            </ul>
        </nav>
    </div>
);

const Banners = ({ category }) => {
    const { db, appId } = useFirebase();
    const [activeBanners, setActiveBanners] = useState([]);

    useEffect(() => {
        if (!db || !appId) return;

        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const bannersRef = collection(db, `artifacts/${appId}/public/data/banners`);
        
        const categoriesToQuery = ['all'];
        if (category) {
            categoriesToQuery.push(category);
        }
        
        const q = query(bannersRef, 
            where('category', 'in', categoriesToQuery),
            where('endDate', '>=', today),
            orderBy('endDate', 'asc') 
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allBanners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const currentBanners = allBanners.filter(b => {
                const isStarted = b.startDate <= today;
                const isDismissed = localStorage.getItem(`banner_${b.id}_dismissed`);
                return isStarted && !isDismissed;
            });

            setActiveBanners(currentBanners.slice(0, 3));
            
        }, (error) => {
            console.error("Error fetching banners.", error);
        });

        return () => unsubscribe();
    }, [db, appId, category]);

    const handleDismiss = (e, bannerId) => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.setItem(`banner_${bannerId}_dismissed`, 'true');
        setActiveBanners(prevBanners => prevBanners.filter(b => b.id !== bannerId));
    };

    if (activeBanners.length === 0) return null;

    return (
        <div className="container mx-auto my-8 max-w-5xl space-y-4">
            {activeBanners.map(banner => (
                <div key={banner.id} className="relative group">
                    <a href={banner.link} target="_blank" rel="noopener noreferrer" className="block w-full h-auto rounded-lg overflow-hidden shadow-lg">
                        <img src={banner.imageUrl} alt="Promotional Banner" className="w-full h-auto object-cover" />
                    </a>
                    <button 
                        onClick={(e) => handleDismiss(e, banner.id)} 
                        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        aria-label="Dismiss Banner"
                    >
                        <X size={20} />
                    </button>
                </div>
            ))}
        </div>
    );
};

const Footer = () => (
    <footer className="bg-green-900 bg-opacity-80 text-white p-6 mt-12 rounded-t-xl">
        <div className="container mx-auto text-center">
            <p className="text-lg mb-2">© {new Date().getFullYear()} GoOutside!. All rights reserved.</p>
            <p className="text-sm">Disclaimer: We are a participant in various affiliate programs, designed to provide a means for us to earn fees by linking to affiliated sites.</p>
            <div className="flex justify-center items-center flex-wrap gap-x-4 gap-y-2 mt-4">
                <Link to="/privacy-policy" className="hover:text-green-300 transition duration-300">Privacy Policy</Link>
                <Link to="/terms-of-service" className="hover:text-green-300 transition duration-300">Terms of Service</Link>
                <Link to="/contact-us" className="hover:text-green-300 transition duration-300">Contact Us</Link>
                <Link to="/about-us" className="hover:text-green-300 transition duration-300">About Us</Link>
                <a href="https://facebook.com/GoOutsideDeals" target="_blank" rel="noopener noreferrer" className="hover:opacity-75 transition duration-300" title="GoOutside Deals on Facebook">
                    <img src="https://lh3.googleusercontent.com/d/1Zjim9N_iaEp8S3lglwgKWwILs_DzgoiU" alt="Facebook Icon" className="h-8 w-8 rounded-full" onError={(e) => { e.target.src = "https://placehold.co/32x32/1877F2/ffffff?text=FB"; }} />
                </a>
            </div>
        </div>
    </footer>
);

const ProductCard = ({ product, onEdit, onDelete, onShare, onReport, isReported }) => {
    const { isAdmin } = useFirebase();
    const savings = product.originalPrice - product.salePrice;
    const savingsPercentage = product.originalPrice > 0 ? ((savings / product.originalPrice) * 100).toFixed(0) : 0;

    return (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300 flex flex-col">
            <Link to={`/product/${product.id}`} className="flex-grow flex flex-col">
                <div className="relative w-full h-48 sm:h-64 overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain transition-transform duration-300 hover:scale-110" loading="lazy" onError={(e) => { e.target.src = `https://placehold.co/400x300/cccccc/333333?text=Image+Not+Available`; }} />
                    {savings > 0 && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">SALE</div>}
                    {product.limitedTimeDeal && <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">Limited Time!</div>}
                    {isAdmin && (
                        <div className="absolute bottom-2 right-2 flex space-x-2">
                            <button onClick={(e) => { e.preventDefault(); onEdit(product); }} className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-md"><Edit size={16} /></button>
                            <button onClick={(e) => { e.preventDefault(); onDelete(product.id); }} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-md"><Trash2 size={16} /></button>
                        </div>
                    )}
                </div>
                <div className="p-4 md:p-6 flex-grow flex flex-col justify-between">
                    <div>
                        <h3 className="text-base md:text-xl font-semibold text-green-800 mb-2 line-clamp-2">{product.name}</h3>
                        <p className="text-gray-500 text-xs mt-1 line-clamp-1">at <span className="font-semibold">{product.store || 'Unknown Store'}</span></p>
                    </div>
                    <div className="mt-4">
                        <div className="flex items-baseline mb-1">
                            <span className="text-gray-500 line-through text-base mr-2">${product.originalPrice.toFixed(2)}</span>
                            <span className="text-green-700 font-bold text-xl md:text-2xl">${product.salePrice.toFixed(2)}</span>
                        </div>
                        {savings > 0 && <p className="text-red-600 font-semibold text-sm">You save {savingsPercentage}%</p>}
                        {product.couponCode && (
                            <p className="text-blue-600 font-semibold text-sm mt-1">
                                Use Code: {product.couponCode}
                            </p>
                        )}
                    </div>
                </div>
            </Link>
            <div className="p-4 pt-0 flex items-center gap-2">
                <a href={product.link} target="_blank" rel="noopener noreferrer" className="flex-grow bg-orange-500 hover:bg-orange-600 text-white text-center py-2 px-3 rounded-full font-bold text-sm transition-all shadow-lg hover:shadow-xl">View Deal</a>
                <button onClick={() => onShare(product)} className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"><Share2 size={18} className="text-gray-600" /></button>
                <button onClick={() => onReport(product)} className={`p-2 rounded-full ${isReported ? 'bg-red-500 text-white cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`} disabled={isReported}><Flag size={18} /></button>
            </div>
        </div>
    );
};

const ExpiredProductCard = ({ product }) => {
    const savings = product.originalPrice - product.salePrice;
    const savingsPercentage = product.originalPrice > 0 ? ((savings / product.originalPrice) * 100).toFixed(0) : 0;

    return (
        <div className="bg-gray-200 rounded-2xl shadow-lg overflow-hidden flex flex-col filter grayscale">
            <div className="relative w-full h-48 sm:h-64 overflow-hidden bg-gray-300 flex items-center justify-center">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.target.src = `https://placehold.co/400x300/cccccc/333333?text=Image+Not+Available`; }} />
                <div className="absolute top-2 left-2 bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">EXPIRED</div>
            </div>
            <div className="p-4 md:p-6 flex-grow flex flex-col justify-between">
                <div>
                    <h3 className="text-base md:text-xl font-semibold text-gray-700 mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-gray-500 text-xs mt-1 line-clamp-1">at <span className="font-semibold">{product.store || 'Unknown Store'}</span></p>
                </div>
                <div className="mt-4">
                    <div className="flex items-baseline mb-1">
                        <span className="text-gray-500 line-through text-base mr-2">${product.originalPrice.toFixed(2)}</span>
                        <span className="text-gray-600 font-bold text-xl md:text-2xl">${product.salePrice.toFixed(2)}</span>
                    </div>
                    {savings > 0 && <p className="text-gray-500 font-semibold text-sm">You saved {savingsPercentage}%</p>}
                </div>
            </div>
        </div>
    );
};

const ProductsList = ({ products, loading, searchTerm, onEdit, onDelete, onShare, onReport, reportedDeals }) => {
    const [sortOption, setSortOption] = useState('newest');

    const filteredAndSortedProducts = useMemo(() => {
        let currentProducts = [...products];
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            currentProducts = currentProducts.filter(p =>
                p.name.toLowerCase().includes(lowerCaseSearchTerm) ||
                p.description.toLowerCase().includes(lowerCaseSearchTerm) ||
                (p.store && p.store.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }
        switch (sortOption) {
            case 'priceLowToHigh': currentProducts.sort((a, b) => a.salePrice - b.salePrice); break;
            case 'priceHighToLow': currentProducts.sort((a, b) => b.salePrice - a.salePrice); break;
            case 'largestDiscount':
                currentProducts.sort((a, b) => {
                    const discountA = a.originalPrice > 0 ? (a.originalPrice - a.salePrice) / a.originalPrice : 0;
                    const discountB = b.originalPrice > 0 ? (b.originalPrice - b.salePrice) / b.originalPrice : 0;
                    return discountB - discountA;
                });
                break;
            default: currentProducts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)); break;
        }
        return currentProducts;
    }, [products, searchTerm, sortOption]);

    return (
        <>
            <div className="relative mb-6 text-left w-full max-w-[240px]">
                <select className="block w-full py-2 px-4 pr-8 rounded-full bg-white bg-opacity-90 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-inner appearance-none" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
                    <option value="newest">Sort by: Newest</option>
                    <option value="priceLowToHigh">Sort by: Price (Low to High)</option>
                    <option value="priceHighToLow">Sort by: Price (High to Low)</option>
                    <option value="largestDiscount">Sort by: Best Discount %</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 6.757 7.586 5.343 9z"/></svg>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-green-700" size={48} /></div>
            ) : filteredAndSortedProducts.length === 0 ? (
                <div className="text-center text-gray-100 text-xl mt-12 bg-black bg-opacity-20 p-8 rounded-xl">No products found. Check your search term or try another category.</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
                    {filteredAndSortedProducts.map((product) => (
                        <ProductCard key={product.id} product={product} onEdit={onEdit} onDelete={onDelete} onShare={onShare} onReport={onReport} isReported={reportedDeals.includes(product.id)} />
                    ))}
                </div>
            )}
        </>
    );
};

// --- Pages ---
const HomePage = (props) => {
    const { products, loading } = useProducts('products', null);
    const { products: expiredProducts } = useProducts('expiredProducts', null);
    return (
        <>
            <Banners category={null} />
            <h2 className="text-5xl font-extrabold text-center text-white mb-12" style={{ textShadow: '3px 3px 6px rgba(0,0,0,0.9)' }}>Latest Outdoor & Fishing Deals</h2>
            <ProductsList products={products} loading={loading} {...props} />
            {expiredProducts.length > 0 && (
                <>
                    <div className="my-12 border-t-2 border-gray-500 border-dashed"></div>
                    <h2 className="text-3xl font-bold text-center text-gray-400 mb-8">Expired Deals Archive</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
                        {expiredProducts.map((product) => (
                            <ExpiredProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const CategoryPage = ({ category, title, description, Icon, ...rest }) => {
    const { products, loading } = useProducts('products', category);
    const { products: expiredProducts } = useProducts('expiredProducts', category);
    return (
        <>
            <Banners category={category} />
            <div className="bg-gradient-to-r from-green-700 to-green-900 text-white p-4 rounded-xl shadow-lg flex items-center justify-center mb-6">
                <h2 className="text-3xl font-extrabold text-center flex items-center tracking-wide" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}><Icon className="mr-3" size={28} /> {title}</h2>
            </div>
            <p className="text-lg text-white text-center mb-8 px-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>{description}</p>
            <ProductsList products={products} loading={loading} {...rest} />
            {expiredProducts.length > 0 && (
                <>
                    <div className="my-12 border-t-2 border-gray-500 border-dashed"></div>
                    <h2 className="text-3xl font-bold text-center text-gray-400 mb-8">Expired Deals Archive</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
                        {expiredProducts.map((product) => (
                            <ExpiredProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const ExpiredDealsPage = () => {
    const { products, loading } = useProducts('expiredProducts', null);
    return (
        <>
            <div className="bg-gradient-to-r from-gray-700 to-gray-900 text-white p-4 rounded-xl shadow-lg flex items-center justify-center mb-6">
                <h2 className="text-3xl font-extrabold text-center flex items-center tracking-wide" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                    <Archive className="mr-3" size={28} /> Expired Deals Archive
                </h2>
            </div>
            <p className="text-lg text-white text-center mb-8 px-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>
                These deals have ended, but check back soon for new offers!
            </p>
            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-green-700" size={48} /></div>
            ) : products.length === 0 ? (
                <div className="text-center text-gray-100 text-xl mt-12 bg-black bg-opacity-20 p-8 rounded-xl">No expired deals found.</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
                    {products.map((product) => (
                        <ExpiredProductCard key={product.id} product={product} />
                    ))}
                </div>
            )}
        </>
    );
};


const ProductPage = ({ onEdit, onShare, onReport, reportedDeals }) => {
    const { productId } = useParams();
    const { db, appId, isAdmin } = useFirebase();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProduct = async () => {
            if (!db || !appId) return;
            try {
                const productRef = doc(db, `artifacts/${appId}/public/data/products`, productId);
                const productSnap = await getDoc(productRef);
                setProduct(productSnap.exists() ? { id: productSnap.id, ...productSnap.data() } : null);
            } catch (error) {
                console.error("Error fetching product:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [productId, db, appId]);

    useEffect(() => {
        if (!product) return;
        document.title = `${product.name} | GoOutside!`;

        const metaDescription = product.description ? product.description.substring(0, 160) + '...' : 'Check out this deal on GoOutside!';
        
        const tags = {
            'og:title': `${product.name} | GoOutside!`,
            'og:description': metaDescription,
            'og:image': product.image || 'https://www.gooutside.us/logo512.png',
            'og:url': window.location.href,
            'twitter:card': 'summary_large_image',
        };

        Object.keys(tags).forEach(key => {
            let meta = document.querySelector(`meta[property='${key}']`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('property', key);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', tags[key]);
        });

    }, [product]);

    if (loading) return <GlobalLoader />;
    if (!product) return (
        <div className="text-center text-white text-3xl font-bold mt-20 bg-black bg-opacity-30 p-10 rounded-xl">
            <p>404 - Product Not Found</p>
            <Link to="/" className="text-orange-300 hover:underline mt-4 inline-block">Go Home</Link>
        </div>
    );

    const savings = product.originalPrice - product.salePrice;
    const savingsPercentage = product.originalPrice > 0 ? ((savings / product.originalPrice) * 100).toFixed(0) : 0;
    const isReported = reportedDeals.includes(productId);

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl">
            <div className="flex justify-between items-center mb-6">
                <button onClick={() => navigate(-1)} className="flex items-center text-green-700 font-semibold hover:text-orange-600"><ArrowLeft size={20} className="mr-2" /> Back</button>
                {isAdmin && <button onClick={() => onEdit(product)} className="flex items-center bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full"><Edit size={18} className="mr-2" /> Edit</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex justify-center items-center bg-gray-100 rounded-xl p-4"><img src={product.image} alt={product.name} className="max-h-96 object-contain" /></div>
                <div>
                    <h1 className="text-4xl font-extrabold text-green-900 mb-4">{product.name}</h1>
                    <p className="text-gray-600 text-lg mb-6">{product.description}</p>
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <div className="flex items-baseline mb-2">
                            <span className="text-gray-500 line-through text-2xl mr-3">${product.originalPrice.toFixed(2)}</span>
                            <span className="text-green-700 font-bold text-4xl">${product.salePrice.toFixed(2)}</span>
                        </div>
                        {savings > 0 && <p className="text-red-600 font-semibold text-xl mb-4">You save {savingsPercentage}% (${savings.toFixed(2)})!</p>}
                        {product.couponCode && (
                            <p className="text-blue-600 font-semibold text-lg my-2">
                                Use Code: {product.couponCode}
                            </p>
                        )}
                        <div className="flex items-center gap-2 mt-6">
                            <a href={product.link} target="_blank" rel="noopener noreferrer" className="flex-grow bg-orange-500 hover:bg-orange-600 text-white text-center py-4 px-6 rounded-full font-bold text-xl">View Deal</a>
                            <button onClick={() => onShare(product)} className="p-4 bg-gray-200 hover:bg-gray-300 rounded-full"><Share2 size={24} /></button>
                            <button onClick={() => onReport(product)} className={`p-4 rounded-full ${isReported ? 'bg-red-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`} disabled={isReported}><Flag size={24} /></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LoginPage = () => {
    const { auth } = useFirebase();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!auth) return;
        setLoading(true);
        setError('');
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate('/');
        } catch (err) {
            setError('Failed to log in. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center py-12">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-2xl shadow-xl">
                <div className="text-center"><h2 className="text-4xl font-extrabold text-green-900">Admin Login</h2></div>
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <input id="email-address" name="email" type="email" required className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                        <input id="password" name="password" type="password" required className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </div>
                    {error && <p className="text-red-500 text-center text-sm">{error}</p>}
                    <div><button type="submit" disabled={loading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400">{loading ? <Loader2 className="animate-spin" /> : 'Sign in'}</button></div>
                </form>
            </div>
        </div>
    );
};

const AboutUsPage = () => <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl"><h2 className="text-4xl font-extrabold text-center text-green-900 mb-8 drop-shadow-md">About Us</h2><div className="prose max-w-none text-gray-700"><p>Welcome to GoOutside! We're your dedicated resource for discovering the best deals on outdoor gear. We tirelessly search the web for discounts on fishing equipment, camping gear, water sports essentials, and more. When you see a deal you like, clicking it takes you directly to the retailer's website to complete your purchase. We don't sell products ourselves; we just connect you with great offers.</p></div></div>;
const ContactUsPage = () => <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl max-w-2xl"><h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Contact Us</h2><div className="text-center"><p className="text-lg">Have questions or feedback? The best way to reach us is by email:</p><p className="text-xl font-bold text-green-800 bg-gray-100 p-4 rounded-lg inline-block mt-4">info [at] gooutside.us</p></div></div>;
const TermsOfServicePage = () => <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl"><h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Terms of Service</h2><div className="prose max-w-none text-gray-700"><p>By accessing this website, we assume you accept these terms and conditions. GoOutside is a participant in various affiliate marketing programs, which means we may get paid commissions on products purchased through our links to retailer sites. Our editorial content is not influenced by advertisers or affiliate partnerships.</p></div></div>;
const PrivacyPolicyPage = () => <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl"><h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Privacy Policy</h2><div className="prose max-w-none text-gray-700"><p>Your privacy is important to us. This Privacy Policy explains how GoOutside collects, uses, and discloses information about you when you visit our website. We collect log information, device information, and use cookies to improve our services. We do not handle your payment or order information directly.</p></div></div>;

const DealReportsPage = () => {
    const { db, appId } = useFirebase();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db || !appId) return;
        const reportsCollectionRef = collection(db, `artifacts/${appId}/public/data/dealReports`);
        const q = query(reportsCollectionRef, orderBy('reportedAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReports(reportsData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const handleDeleteReport = async (reportId) => {
        if (!db || !appId) return;
        const reportRef = doc(db, `artifacts/${appId}/public/data/dealReports`, reportId);
        try {
            await deleteDoc(reportRef);
        } catch (error) {
            console.error("Error deleting report:", error);
        }
    };

    if (loading) return <GlobalLoader />;

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl">
            <h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Deal Reports</h2>
            {reports.length === 0 ? (
                <p className="text-center text-gray-600">No deals have been reported.</p>
            ) : (
                <ul className="space-y-4">
                    {reports.map(report => (
                        <li key={report.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                            <div>
                                <Link to={`/product/${report.productId}`} className="font-bold text-green-800 hover:underline">{report.productName}</Link>
                                <p className="text-sm text-gray-500">Reported on: {new Date(report.reportedAt?.seconds * 1000).toLocaleString()}</p>
                            </div>
                            <button onClick={() => handleDeleteReport(report.id)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full"><Trash2 size={16} /></button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ManageBannersPage = () => {
    const { db, appId } = useFirebase();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageUrl, setImageUrl] = useState('');
    const [link, setLink] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [category, setCategory] = useState('all');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);

    useEffect(() => {
        if (!db || !appId) return;
        const bannersRef = collection(db, `artifacts/${appId}/public/data/banners`);
        const q = query(bannersRef, orderBy('startDate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBanners(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const resetForm = () => {
        setImageUrl('');
        setLink('');
        setStartDate('');
        setEndDate('');
        setCategory('all');
        setEditingBanner(null);
    };

    const handleEditClick = (banner) => {
        setEditingBanner(banner);
        setImageUrl(banner.imageUrl);
        setLink(banner.link);
        setStartDate(banner.startDate);
        setEndDate(banner.endDate);
        setCategory(banner.category);
        window.scrollTo(0, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!imageUrl || !link || !startDate || !endDate) {
            alert("Please fill out all fields.");
            return;
        }
        setIsSubmitting(true);
        const bannerData = { imageUrl, link, startDate, endDate, category };

        try {
            if (editingBanner) {
                const bannerRef = doc(db, `artifacts/${appId}/public/data/banners`, editingBanner.id);
                await updateDoc(bannerRef, bannerData);
            } else {
                const bannersRef = collection(db, `artifacts/${appId}/public/data/banners`);
                await addDoc(bannersRef, bannerData);
            }
            resetForm();
        } catch (error) {
            console.error("Error saving banner:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (bannerId) => {
        if (window.confirm("Are you sure you want to delete this banner?")) {
            const bannerRef = doc(db, `artifacts/${appId}/public/data/banners`, bannerId);
            await deleteDoc(bannerRef);
        }
    };

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl">
            <h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Manage Banners</h2>
            
            <div className="max-w-2xl mx-auto mb-12 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-2xl font-bold text-green-800 mb-4">{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block font-bold mb-1">Image URL</label>
                        <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="w-full p-2 border rounded" placeholder="https://example.com/banner.jpg" />
                    </div>
                    <div>
                        <label className="block font-bold mb-1">Link URL</label>
                        <input type="url" value={link} onChange={e => setLink(e.target.value)} className="w-full p-2 border rounded" placeholder="https://example.com/sale" />
                    </div>
                    <div>
                        <label className="block font-bold mb-1">Category</label>
                        <select value={category} onChange={e => setCategory(e.target.value)} className="w-full p-2 border rounded bg-white">
                            <option value="all">All Pages</option>
                            <option value="fishing">Fishing</option>
                            <option value="camping">Camping</option>
                            <option value="waterSports">Water Sports</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block font-bold mb-1">Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 border rounded" />
                        </div>
                        <div>
                            <label className="block font-bold mb-1">End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border rounded" />
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <button type="submit" disabled={isSubmitting} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-full flex-grow">
                            {isSubmitting ? <Loader2 className="animate-spin mx-auto" /> : (editingBanner ? 'Update Banner' : 'Add Banner')}
                        </button>
                        {editingBanner && (
                            <button type="button" onClick={resetForm} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-full">
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div>
                <h3 className="text-2xl font-bold text-green-800 mb-4">Current & Scheduled Banners</h3>
                {loading ? <GlobalLoader /> : (
                    <ul className="space-y-4">
                        {banners.map(banner => (
                            <li key={banner.id} className="bg-gray-100 p-4 rounded-lg flex flex-col md:flex-row justify-between items-center">
                                <img src={banner.imageUrl} alt="Banner preview" className="w-full md:w-48 h-auto rounded-md mb-4 md:mb-0 md:mr-4" />
                                <div className="flex-grow text-center md:text-left">
                                    <p className="font-bold text-lg truncate" title={banner.link}>{banner.link}</p>
                                    <p className="text-sm text-gray-600">Category: <span className="font-semibold">{banner.category}</span></p>
                                    <p className="text-sm text-gray-600">Active: {banner.startDate} to {banner.endDate}</p>
                                </div>
                                <div className="flex gap-2 mt-4 md:mt-0">
                                    <button onClick={() => handleEditClick(banner)} className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"><Edit size={16} /></button>
                                    <button onClick={() => handleDelete(banner.id)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full"><Trash2 size={16} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

// --- Main App Component ---
function App() {
    const { isAdmin, db, appId } = useFirebase();
    const location = useLocation();
    const navigate = useNavigate();

    const [showProductForm, setShowProductForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productToDelete, setProductToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [message, setMessage] = useState({ text: '', type: '' });
    const [reportedDeals, setReportedDeals] = useState(() => JSON.parse(localStorage.getItem('reportedDeals')) || []);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setShowProductForm(true);
        navigate('/');
        setTimeout(() => window.scrollTo(0, 0), 0);
    };

    const confirmDelete = async () => {
        if (!db || !appId || !productToDelete) return;

        const productRef = doc(db, `artifacts/${appId}/public/data/products`, productToDelete);
        const expiredProductsRef = collection(db, `artifacts/${appId}/public/data/expiredProducts`);

        try {
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
                const productData = productSnap.data();
                await addDoc(expiredProductsRef, {
                    ...productData,
                    expiredAt: serverTimestamp()
                });
                await deleteDoc(productRef);
                showMessage("Product archived successfully!");
            }
        } catch (error) {
            showMessage("Failed to archive product.", "error");
            console.error("Error archiving product:", error);
        } finally {
            setProductToDelete(null);
        }
    };

    const handleShare = async (product) => {
        const shareData = {
            title: product.name,
            text: `Check out this deal on the ${product.name}!`,
            url: `${window.location.origin}/product/${product.id}`
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareData.url);
                showMessage('Link copied to clipboard!');
            } catch (err) {
                showMessage('Failed to copy link.', 'error');
            }
        }
    };

    const handleReport = async (product) => {
        if (reportedDeals.includes(product.id)) return;
        const newReportedDeals = [...reportedDeals, product.id];
        setReportedDeals(newReportedDeals);
        localStorage.setItem('reportedDeals', JSON.stringify(newReportedDeals));

        if (!db || !appId) {
            showMessage('Error: Could not connect to the database.', 'error');
            return;
        }
        try {
            const reportsCollectionRef = collection(db, `artifacts/${appId}/public/data/dealReports`);
            await addDoc(reportsCollectionRef, {
                productId: product.id,
                productName: product.name,
                reportedAt: serverTimestamp(),
            });
            showMessage('Deal reported. Thank you!');
        } catch (error) {
            console.error("Error reporting deal:", error);
            showMessage('Could not submit report.', 'error');
        }
    };
    
    const handleFormClose = () => {
        setShowProductForm(false);
        setEditingProduct(null);
    };
    
    const handleFormSuccess = (msg) => {
        handleFormClose();
        showMessage(msg, 'success');
    };

    const backgroundImages = {
        '/': 'https://lh3.googleusercontent.com/d/1HuUOENeHwOTHoe4MVOE9YqU_Ff4NYlpB',
        '/fishing-deals': 'https://lh3.googleusercontent.com/d/1MeRVJ02yLiL0aUhJ5_HBLE5aFlksrEnz',
        '/camping-deals': 'https://lh3.googleusercontent.com/d/1twZcjNrudmXFLr4LRDXPfRgOJyhebtrJ',
        '/water-sports-deals': 'https://lh3.googleusercontent.com/d/1Zy-LNMRE3oj-5Nj_t6s8KzOSnVcnWCbH',
    };
    const currentBg = backgroundImages[location.pathname] || backgroundImages['/'];
    
    const commonPageProps = { searchTerm: debouncedSearchTerm, onEdit: handleEdit, onDelete: (id) => setProductToDelete(id), onShare: handleShare, onReport: handleReport, reportedDeals };

    return (
        <div className="min-h-screen text-gray-800 transition-all duration-500" style={{ fontFamily: "'Roboto', sans-serif", backgroundImage: `url(${currentBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
            <div className="min-h-screen bg-black bg-opacity-40">
                <Header searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                <CategoryNav />
                <main className="container mx-auto p-6 mt-8">
                    {isAdmin && location.pathname === '/' && (
                        <div className="flex justify-center mb-8">
                            <button onClick={() => { setEditingProduct(null); setShowProductForm(!showProductForm); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-full shadow-lg">
                                {showProductForm && !editingProduct ? 'Cancel' : 'Add New Product'}
                            </button>
                        </div>
                    )}
                    
                    {showProductForm && <ProductForm product={editingProduct} onSuccess={handleFormSuccess} onCancel={handleFormClose} />}
                    
                    {message.text && <div className={`p-4 mb-6 rounded-lg text-center font-semibold ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message.text}</div>}

                    <Routes>
                        <Route path="/" element={<HomePage {...commonPageProps} />} />
                        <Route path="/product/:productId" element={<ProductPage {...commonPageProps} />} />
                        <Route path="/fishing-deals" element={<CategoryPage category="fishing" title="Fishing Deals" description="The best offers on rods, reels, and more." Icon={Fish} {...commonPageProps} />} />
                        <Route path="/camping-deals" element={<CategoryPage category="camping" title="Camping Deals" description="Amazing deals on tents, sleeping bags, and more." Icon={Tent} {...commonPageProps} />} />
                        <Route path="/water-sports-deals" element={<CategoryPage category="waterSports" title="Water Sports Deals" description="Discounts on kayaks, paddleboards, and more." Icon={Waves} {...commonPageProps} />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/about-us" element={<AboutUsPage />} />
                        <Route path="/contact-us" element={<ContactUsPage />} />
                        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        {isAdmin && <Route path="/deal-reports" element={<DealReportsPage />} />}
                        {isAdmin && <Route path="/banners" element={<ManageBannersPage />} />}
                        {isAdmin && <Route path="/manage-articles" element={<ManageArticlesPage showMessage={showMessage} />} />}
                        <Route path="/expired-deals" element={<ExpiredDealsPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:articleId" element={<SingleArticlePage showMessage={showMessage} />} />
                        <Route path="*" element={<div className="text-center text-white text-3xl font-bold mt-20">404 - Page Not Found</div>} />
                    </Routes>
                </main>
                <Footer />
                {productToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-sm mx-auto">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Deletion</h3>
                            <p className="text-gray-600 mb-6">Are you sure you want to archive this product?</p>
                            <div className="flex justify-center space-x-4">
                                <button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-5 rounded-full">Archive</button>
                                <button onClick={() => setProductToDelete(null)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-5 rounded-full">Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const ProductForm = ({ product, onSuccess, onCancel }) => {
    const { db, appId } = useFirebase();
    const [formData, setFormData] = useState({ name: '', originalPrice: '', salePrice: '', image: '', description: '', link: '', store: '', category: [], couponCode: '', couponDiscount: '', limitedTimeDeal: false });
    const [loading, setLoading] = useState(false);
    const isEditing = !!product;

    useEffect(() => {
        if (isEditing) {
            setFormData({
                name: product.name || '',
                originalPrice: product.originalPrice || '',
                salePrice: product.salePrice || '',
                image: product.image || '',
                description: product.description || '',
                link: product.link || '',
                store: product.store || '',
                category: product.category || [],
                couponCode: product.couponCode || '',
                couponDiscount: product.couponDiscount || '',
                limitedTimeDeal: product.limitedTimeDeal || false,
            });
        } else {
            setFormData({ name: '', originalPrice: '', salePrice: '', image: '', description: '', link: '', store: '', category: [], couponCode: '', couponDiscount: '', limitedTimeDeal: false });
        }
    }, [product, isEditing]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const handleCategoryChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => ({ ...prev, category: checked ? [...prev.category, value] : prev.category.filter(c => c !== value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const productData = { ...formData, originalPrice: parseFloat(formData.originalPrice), salePrice: parseFloat(formData.salePrice) };

        try {
            const productsCollectionRef = collection(db, `artifacts/${appId}/public/data/products`);
            if (isEditing) {
                const productRef = doc(db, `artifacts/${appId}/public/data/products`, product.id);
                await updateDoc(productRef, productData);
                onSuccess("Product updated successfully!");
            } else {
                await addDoc(productsCollectionRef, { ...productData, createdAt: serverTimestamp() });
                onSuccess("Product added successfully!");
            }
        } catch (error) {
            console.error("Error saving product:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const availableCategories = [{ value: 'fishing', label: 'Fishing' }, { value: 'camping', label: 'Camping' }, { value: 'waterSports', label: 'Water Sports' }];

    return (
        <div className="bg-white p-8 rounded-2xl shadow-xl mb-12 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-green-800 mb-6 text-center">{isEditing ? 'Edit Product' : 'Add New Product'}</h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2"><label className="block text-sm font-bold mb-2">Product Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3"/></div>
                <div><label className="block text-sm font-bold mb-2">Original Price ($)</label><input type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3" step="0.01"/></div>
                <div><label className="block text-sm font-bold mb-2">Sale Price ($)</label><input type="number" name="salePrice" value={formData.salePrice} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3" step="0.01"/></div>
                <div className="md:col-span-2"><label className="block text-sm font-bold mb-2">Image URL</label><input type="url" name="image" value={formData.image} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3"/></div>
                <div className="md:col-span-2"><label className="block text-sm font-bold mb-2">Affiliate Link</label><input type="url" name="link" value={formData.link} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3"/></div>
                <div className="md:col-span-2"><label className="block text-sm font-bold mb-2">Online Store (e.g., Cabela's)</label><input type="text" name="store" value={formData.store} onChange={handleChange} className="shadow border rounded-lg w-full py-2 px-3"/></div>
                <div className="md:col-span-2"><label className="block text-sm font-bold mb-2">Description</label><textarea name="description" value={formData.description} onChange={handleChange} required className="shadow border rounded-lg w-full py-2 px-3 h-24"></textarea></div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-bold mb-2">Categories</label>
                    <div className="flex flex-wrap gap-4">{availableCategories.map(cat => (<label key={cat.value} className="flex items-center"><input type="checkbox" value={cat.value} checked={formData.category.includes(cat.value)} onChange={handleCategoryChange} className="mr-2 h-5 w-5"/>{cat.label}</label>))}</div>
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-bold mb-2">Coupon Discount (e.g., 20% Off)</label>
                        <input 
                            type="text" 
                            name="couponDiscount" 
                            value={formData.couponDiscount} 
                            onChange={handleChange} 
                            className="shadow border rounded-lg w-full py-2 px-3"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold mb-2">Coupon Code</label>
                        <input 
                            type="text" 
                            name="couponCode" 
                            value={formData.couponCode} 
                            onChange={handleChange} 
                            className="shadow border rounded-lg w-full py-2 px-3"
                        />
                    </div>
                </div>
                <div className="md:col-span-2 flex items-center">
                    <input type="checkbox" name="limitedTimeDeal" checked={formData.limitedTimeDeal} onChange={handleChange} className="mr-2 h-5 w-5" />
                    <label className="text-sm font-bold">Limited Time Deal?</label>
                </div>
                <div className="md:col-span-2 text-center flex gap-4 justify-center">
                    <button type="submit" disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full">{loading ? <Loader2 className="animate-spin"/> : (isEditing ? 'Update Product' : 'Add Product')}</button>
                    <button type="button" onClick={onCancel} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-8 rounded-full">Cancel</button>
                </div>
            </form>
        </div>
    );
};

const ManageArticlesPage = ({ showMessage }) => {
    const { db, appId } = useFirebase();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [formData, setFormData] = useState({ title: '', author: '', content: '' });

    useEffect(() => {
        if (!db || !appId) return;
        const articlesRef = collection(db, `artifacts/${appId}/public/data/articles`);
        const q = query(articlesRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const resetForm = () => {
        setFormData({ title: '', author: '', content: '' });
        setEditingArticle(null);
    };

    const handleEditClick = (article) => {
        setEditingArticle(article);
        setFormData({
            title: article.title,
            author: article.author,
            content: article.content,
        });
        window.scrollTo(0, 0);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.content) {
            showMessage("Please fill out the title and content fields.", "error");
            return;
        }
        setIsSubmitting(true);
        const articleData = { ...formData };

        try {
            if (editingArticle) {
                const articleRef = doc(db, `artifacts/${appId}/public/data/articles`, editingArticle.id);
                await updateDoc(articleRef, articleData);
                showMessage("Article updated successfully!");
            } else {
                const articlesRef = collection(db, `artifacts/${appId}/public/data/articles`);
                await addDoc(articlesRef, { ...articleData, createdAt: serverTimestamp() });
                showMessage("Article published successfully!");
            }
            resetForm();
        } catch (error) {
            console.error("Error saving article:", error);
            showMessage(`Error: ${error.message}`, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (articleId) => {
        if (window.confirm("Are you sure you want to delete this article?")) {
            try {
                const articleRef = doc(db, `artifacts/${appId}/public/data/articles`, articleId);
                await deleteDoc(articleRef);
                showMessage("Article deleted successfully.");
            } catch (error) {
                console.error("Error deleting article:", error);
                showMessage(`Error: ${error.message}`, "error");
            }
        }
    };

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl">
            <h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Manage Articles</h2>
            <div className="max-w-3xl mx-auto mb-12 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-2xl font-bold text-green-800 mb-4">{editingArticle ? 'Edit Article' : 'Add New Article'}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" name="title" placeholder="Article Title" value={formData.title} onChange={handleChange} className="w-full p-2 border rounded" required />
                    <input type="text" name="author" placeholder="Author Name" value={formData.author} onChange={handleChange} className="w-full p-2 border rounded" />
                    <textarea 
                        name="content" 
                        placeholder="Use tags for formatting. Separate paragraphs with a blank line."
                        value={formData.content} 
                        onChange={handleChange} 
                        className="w-full p-2 border rounded h-64" 
                        required
                    />
                    <div className="flex gap-4">
                        <button type="submit" disabled={isSubmitting} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-full flex-grow">
                            {isSubmitting ? <Loader2 className="animate-spin mx-auto" /> : (editingArticle ? 'Update Article' : 'Publish Article')}
                        </button>
                        {editingArticle && <button type="button" onClick={resetForm} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-full">Cancel Edit</button>}
                    </div>
                </form>
                 <div className="mt-6 p-4 bg-gray-100 rounded-lg text-sm text-gray-700">
                    <h4 className="font-bold mb-2">Formatting Guide:</h4>
                    <ul className="list-disc list-inside space-y-1">
                        <li><strong>Bold:</strong> `[B]your text[/B]`</li>
                        <li><strong>Underline:</strong> `[U]your text[/U]`</li>
                        <li><strong>Heading 1:</strong> `[H1]your text[/H1]`</li>
                        <li><strong>Heading 2:</strong> `[H2]your text[/H2]`</li>
                        <li><strong>Image:</strong> `[IMG:https://...url]`</li>
                        <li><strong>YouTube Video:</strong> `[YOUTUBE:videoId]`</li>
                        <li><strong>Link:</strong> `[LINK:https://...url|display text]`</li>
                        <li><strong>Paragraphs:</strong> Leave a blank line between paragraphs.</li>
                        <li><strong>Line Break:</strong> Press Enter once for a single line break.</li>
                    </ul>
                </div>
            </div>
            <div>
                <h3 className="text-2xl font-bold text-green-800 mb-4">Published Articles</h3>
                {loading ? <GlobalLoader /> : (
                    <ul className="space-y-4">
                        {articles.map(article => (
                            <li key={article.id} className="bg-gray-100 p-4 rounded-lg flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-lg">{article.title}</p>
                                    <p className="text-sm text-gray-600">By {article.author || 'Unknown'} on {new Date(article.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEditClick(article)} className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"><Edit size={16} /></button>
                                    <button onClick={() => handleDelete(article.id)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full"><Trash2 size={16} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};


const BlogPage = () => {
    const { db, appId } = useFirebase();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db || !appId) return;
        const articlesRef = collection(db, `artifacts/${appId}/public/data/articles`);
        const q = query(articlesRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const getPreviewImage = (content) => {
        if (!content) return null;
        const match = content.match(/\[IMG:(.*?)\]/);
        return match ? match[1] : null;
    };

    const getPreviewSummary = (content) => {
        if (!content) return '';
        const paragraphs = content.split(/\n\s*\n/);
        const firstTextParagraph = paragraphs.find(p => p.trim() && !p.trim().startsWith('[IMG:'));
        if (!firstTextParagraph) return '';
        // Strip all tags for the summary
        return firstTextParagraph
            .replace(/\[(B|U|H1|H2)\]/g, '')
            .replace(/\[\/(B|U|H1|H2)\]/g, '')
            .replace(/\[LINK:.*?\|(.*?)\]/g, '$1')
            .substring(0, 150) + '...';
    };

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl">
            <h2 className="text-4xl font-extrabold text-center text-green-900 mb-8">Blog & Buying Guides</h2>
            {loading ? <GlobalLoader /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {articles.map(article => {
                        const imageUrl = getPreviewImage(article.content);
                        const summary = getPreviewSummary(article.content);
                        return (
                            <Link key={article.id} to={`/blog/${article.id}`} className="bg-gray-50 rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 flex flex-col">
                                <img src={imageUrl || 'https://placehold.co/600x400/cccccc/333333?text=Article'} alt={article.title} className="w-full h-48 object-contain bg-gray-100" />
                                <div className="p-6 flex-grow">
                                    <h3 className="text-2xl font-bold text-green-800 mb-2">{article.title}</h3>
                                    <p className="text-gray-600">{summary}</p>
                                </div>
                            </Link>
                        )
                    })}
                </div>
            )}
        </div>
    );
};

const SingleArticlePage = ({ showMessage }) => {
    const { articleId } = useParams();
    const { db, appId } = useFirebase();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!article) return;
        document.title = `${article.title} | GoOutside!`;
        
        const getPreviewImage = (content) => {
            if (!content) return 'https://www.gooutside.us/logo512.png';
            const match = content.match(/\[IMG:(.*?)\]/);
            return match ? match[1] : 'https://www.gooutside.us/logo512.png';
        };
        const getPreviewSummary = (content) => {
            if (!content) return '';
            const paragraphs = content.split(/\n\s*\n/);
            const firstTextParagraph = paragraphs.find(p => p.trim() && !p.trim().startsWith('[IMG:'));
            if (!firstTextParagraph) return 'Your home for the best outdoor and fishing deals.';
            return firstTextParagraph
                .replace(/\[(B|U|H1|H2|YOUTUBE:.*?)\]/g, '')
                .replace(/\[\/(B|U|H1|H2)\]/g, '')
                .replace(/\[LINK:.*?\|(.*?)\]/g, '$1')
                .substring(0, 160) + '...';
        };

        const summary = getPreviewSummary(article.content);
        const imageUrl = getPreviewImage(article.content);

        const tags = {
            'og:title': `${article.title} | GoOutside!`,
            'og:description': summary,
            'og:image': imageUrl,
            'og:url': window.location.href,
            'twitter:card': 'summary_large_image',
        };

        Object.keys(tags).forEach(key => {
            let meta = document.querySelector(`meta[property='${key}']`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('property', key);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', tags[key]);
        });

    }, [article]);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!db || !appId) return;
            try {
                const articleRef = doc(db, `artifacts/${appId}/public/data/articles`, articleId);
                const articleSnap = await getDoc(articleRef);
                setArticle(articleSnap.exists() ? { id: articleSnap.id, ...articleSnap.data() } : null);
            } catch (error) {
                console.error("Error fetching article:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [articleId, db, appId]);

    const handleArticleShare = async () => {
        if (!article) return;
        const shareData = {
            title: article.title,
            text: `Check out this article from GoOutside!: ${article.title}`,
            url: window.location.href
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareData.url);
                showMessage('Link copied to clipboard!');
            } catch (err) {
                showMessage('Failed to copy link.', 'error');
            }
        }
    };

    const renderContent = (content) => {
        if (!content) return null;

        const inlineTagRegex = /(\[LINK:.*?\|.*?\]|\[B\].*?\[\/B\]|\[U\].*?\[\/U\])/g;

        const parseInlineTags = (text, keyPrefix) => {
            if (!text) return text;
            const parts = text.split(inlineTagRegex);
            return parts.map((part, index) => {
                if (part.startsWith('[LINK:')) {
                    const [, url, linkText] = part.match(/\[LINK:(.*?)\|(.*?)\]/);
                    return <a key={`${keyPrefix}-link-${index}`} href={url} target="_blank" rel="noopener noreferrer" className="text-orange-600 font-semibold hover:underline">{linkText}</a>;
                }
                if (part.startsWith('[B]')) {
                    const boldText = part.match(/\[B\](.*?)\[\/B\]/)[1];
                    return <strong key={`${keyPrefix}-b-${index}`}>{boldText}</strong>;
                }
                if (part.startsWith('[U]')) {
                    const underlineText = part.match(/\[U\](.*?)\[\/U\]/)[1];
                    return <u key={`${keyPrefix}-u-${index}`}>{underlineText}</u>;
                }
                return part; // Plain text
            });
        };
        
        const normalizedContent = content.replace(/\r\n/g, '\n');
        const blocks = normalizedContent.split('\n\n');

        return blocks.map((block, bIndex) => {
            const trimmedBlock = block.trim();
            if (!trimmedBlock) return null;

            if (trimmedBlock.startsWith('[IMG:')) {
                const url = trimmedBlock.match(/\[IMG:(.*?)\]/)[1];
                return (
                    <div key={`img-container-${bIndex}`} className="my-6 flex justify-center">
                        <img 
                            src={url} 
                            alt={`${article.title} content ${bIndex}`} 
                            className="max-w-full max-h-[500px] w-auto h-auto rounded-lg shadow-lg" 
                        />
                    </div>
                );
            }
             if (trimmedBlock.startsWith('[YOUTUBE:')) {
                const videoId = trimmedBlock.match(/\[YOUTUBE:(.*?)\]/)[1];
                return (
                    <div key={`youtube-${bIndex}`} className="my-6 aspect-w-16 aspect-h-9">
                        <iframe 
                            src={`https://www.youtube.com/embed/${videoId}`} 
                            title="YouTube video player" 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen
                            className="w-full h-full rounded-lg shadow-lg"
                        ></iframe>
                    </div>
                );
            }
            if (trimmedBlock.startsWith('[H1]')) {
                const text = trimmedBlock.match(/\[H1\](.*?)\[\/H1\]/)[1];
                return <h1 key={`h1-${bIndex}`} className="text-3xl font-bold mt-6 mb-3">{parseInlineTags(text, `h1-${bIndex}`)}</h1>;
            }
            if (trimmedBlock.startsWith('[H2]')) {
                const text = trimmedBlock.match(/\[H2\](.*?)\[\/H2\]/)[1];
                return <h2 key={`h2-${bIndex}`} className="text-2xl font-bold mt-5 mb-2">{parseInlineTags(text, `h2-${bIndex}`)}</h2>;
            }

            return (
                <p key={`p-${bIndex}`} className="mb-4">
                    {trimmedBlock.split('\n').map((line, lineIndex, arr) => (
                        <React.Fragment key={lineIndex}>
                            {parseInlineTags(line, `p-${bIndex}-l-${lineIndex}`)}
                            {lineIndex < arr.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </p>
            );
        });
    };

    if (loading) return <GlobalLoader />;
    if (!article) return <div className="text-center text-white text-3xl font-bold mt-20">404 - Page Not Found</div>;

    return (
        <div className="container mx-auto p-6 mt-8 bg-white rounded-2xl shadow-xl max-w-4xl">
             <button onClick={() => navigate(-1)} className="flex items-center text-green-700 font-semibold hover:text-orange-600 mb-6"><ArrowLeft size={20} className="mr-2" /> Back to Blog</button>
            <article className="prose max-w-none">
                <h1 className="text-4xl font-extrabold text-green-900">{article.title}</h1>
                <div className="flex justify-between items-center my-4">
                    <p className="text-gray-500">By {article.author || 'GoOutside Staff'} on {new Date(article.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                    <button onClick={handleArticleShare} className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-full">
                        <Share2 size={18} />
                        Share
                    </button>
                </div>
                {renderContent(article.content)}
            </article>
        </div>
    );
};


const AppWrapper = () => (
    <Router>
        <FirebaseProvider>
            <App />
        </FirebaseProvider>
    </Router>
);

export default AppWrapper;
